import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Calendar, MapPin, Edit, Trash2, Power, PowerOff, Plus, Shield, Eye, EyeOff, BarChart3, ExternalLink } from 'lucide-react';

export default function OrganizerExhibitionsList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [exhibitions, setExhibitions] = useState([]);
  const [occupancyMap, setOccupancyMap] = useState({});  // exhibitionId -> {sold, reserved, total, pct}
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [venueCenters, setVenueCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedExhibition, setSelectedExhibition] = useState(null);
  const [ultraPassword, setUltraPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showUltraPassword, setShowUltraPassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [exRes, countryRes, cityRes, venueRes] = await Promise.all([
        axios.get(`${API}/exhibitions`, { headers }),
        axios.get(`${API}/countries`),
        axios.get(`${API}/cities`),
        axios.get(`${API}/venue-centers`)
      ]);

      setExhibitions(exRes.data);
      setCountries(countryRes.data);
      setCities(cityRes.data);
      setVenueCenters(venueRes.data);

      // Fetch occupancy from dashboard overview
      try {
        const dashRes = await axios.get(`${API}/dashboard/overview`, { headers });
        const map = {};
        (dashRes.data.exhibition_comparison || []).forEach(ex => {
          map[ex.id] = {
            sold: ex.sold,
            reserved: ex.reserved,
            total: ex.total_stands,
            pct: ex.occupancy_pct,
          };
        });
        setOccupancyMap(map);
      } catch { /* silently skip */ }

      setLoading(false);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
      setLoading(false);
    }
  };

  const getLocationInfo = (exhibition) => {
    const country = countries.find(c => c.id === exhibition.country_id);
    const city = cities.find(c => c.id === exhibition.city_id);
    const venue = venueCenters.find(v => v.id === exhibition.venue_center_id);
    
    return {
      country: country ? `${country.flag} ${country.name}` : '',
      city: city?.name || '',
      venue: venue?.name || ''
    };
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/exhibitions/${id}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Fuar ${newStatus === 'active' ? 'aktif' : 'pasif'} edildi`);
      fetchData();
    } catch (error) {
      toast.error('Durum değiştirilemedi');
    }
  };

  const handleDelete = async (id) => {
    setSelectedExhibition(id);
    setPasswordError('');
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!ultraPassword) {
      setPasswordError('Ultra Password giriniz');
      return;
    }

    setPasswordError('');

    try {
      const token = localStorage.getItem('token');
      
      // Verify ultra password first
      await axios.post(`${API}/settings/verify-ultra-password`, {
        ultra_password: ultraPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // If valid, delete exhibition
      await axios.delete(`${API}/exhibitions/${selectedExhibition}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Fuar silindi');
      setDeleteDialog(false);
      setUltraPassword('');
      setSelectedExhibition(null);
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        setPasswordError('Hatalı Ultra Password!');
      } else {
        setPasswordError('Fuar silinemedi');
      }
    }
  };

  const handleEdit = (exhibitionId) => {
    navigate(`/organizer/edit/${exhibitionId}`);
  };

  return (
    <div className="p-8" data-testid="organizer-exhibitions-list">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900">Tüm Fuarlarım</h1>
        <p className="text-gray-500 mt-2">Oluşturduğunuz fuarları yönetin</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
        </div>
      ) : exhibitions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-4">Henüz fuar oluşturmadınız</p>
            <Button onClick={() => navigate('/organizer/new-exhibition')}>
              <Plus className="w-4 h-4 mr-2" />
              İlk Fuarınızı Oluşturun
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {exhibitions.map((exhibition) => {
            const location = getLocationInfo(exhibition);
            return (
              <Card 
                key={exhibition.id} 
                className="hover:shadow-xl transition-shadow"
                data-testid={`exhibition-card-${exhibition.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-xl">{exhibition.name}</CardTitle>
                    <Badge 
                      variant={exhibition.status === 'active' ? 'success' : 'default'}
                      data-testid={`status-badge-${exhibition.id}`}
                    >
                      {exhibition.status === 'active' ? 'Aktif' : 'Pasif'}
                    </Badge>
                  </div>
                  <CardDescription className="space-y-1 mt-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{location.venue}</span>
                    </div>
                    <div className="text-sm">
                      {location.city}, {location.country}
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{exhibition.start_date} - {exhibition.end_date}</span>
                    </div>
                    
                    {exhibition.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">{exhibition.description}</p>
                    )}

                    {/* Occupancy bar */}
                    {occupancyMap[exhibition.id] && occupancyMap[exhibition.id].total > 0 && (
                      <div className="space-y-1" data-testid={`occupancy-bar-${exhibition.id}`}>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span className="flex items-center gap-2">
                            <span className="text-red-500 font-medium">{occupancyMap[exhibition.id].sold} satıldı</span>
                            <span className="text-amber-500 font-medium">{occupancyMap[exhibition.id].reserved} rezerve</span>
                            <span className="text-gray-400">/ {occupancyMap[exhibition.id].total} stand</span>
                          </span>
                          <span className="font-semibold text-teal-600">{occupancyMap[exhibition.id].pct}%</span>
                        </div>
                        <Progress
                          value={occupancyMap[exhibition.id].pct}
                          className="h-1.5"
                        />
                      </div>
                    )}

                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(exhibition.id)}
                        className="flex-1"
                        data-testid={`edit-btn-${exhibition.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Düzenle
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        title="Herkese Açık Harita"
                        onClick={() => window.open(`/expo/${exhibition.id}/map`, '_blank')}
                        data-testid={`map-btn-${exhibition.id}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/organizer/report/${exhibition.id}`)}
                        data-testid={`report-btn-${exhibition.id}`}
                        title="Doluluk Raporu"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={exhibition.status === 'active' ? 'outline' : 'default'}
                        onClick={() => handleToggleStatus(exhibition.id, exhibition.status)}
                        data-testid={`toggle-status-${exhibition.id}`}
                      >
                        {exhibition.status === 'active' ? 
                          <PowerOff className="w-4 h-4" /> : 
                          <Power className="w-4 h-4" />
                        }
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(exhibition.id)}
                        data-testid={`delete-btn-${exhibition.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal with Ultra Password */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent data-testid="delete-confirmation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              Fuar Silme Onayı
            </DialogTitle>
            <DialogDescription>
              Bu işlem geri alınamaz. Devam etmek için Ultra Password giriniz.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ultra-password">Ultra Password (6 Haneli)</Label>
              <div className="relative">
                <Input
                  id="ultra-password"
                  type={showUltraPassword ? "text" : "password"}
                  value={ultraPassword}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setUltraPassword(value);
                    setPasswordError(''); // Clear error on input
                  }}
                  maxLength={6}
                  placeholder="******"
                  data-testid="ultra-password-delete-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowUltraPassword(!showUltraPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showUltraPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordError && (
                <p className="text-xs text-red-500" data-testid="password-error">
                  {passwordError}
                </p>
              )}
              <p className="text-xs text-gray-500">
                Ultra Password'u bilmiyorsanız Ultra Admin'e başvurun
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialog(false);
                  setUltraPassword('');
                  setPasswordError('');
                  setSelectedExhibition(null);
                }}
                className="flex-1"
                data-testid="cancel-delete-btn"
              >
                Vazgeç
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                className="flex-1"
                data-testid="confirm-delete-btn"
              >
                Evet, Sil
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}